import {
  createSlice,
  // current as currentLog,
  PayloadAction,
} from '@reduxjs/toolkit';
import { IPosOrder } from '../interface/orders/pos-orders.interface';
import { Moment } from 'moment';

const isSamePrice = (firstPrice: number, secondPrice: number) =>
  Math.abs(firstPrice - secondPrice) <= 0.005;

const isSameRemarks = (firstRemarks = '', secondRemarks = '') =>
  firstRemarks.trim() === secondRemarks.trim();

export const isSamePOSOrderLine = (
  firstOrder: IPosOrder,
  secondOrder: Pick<
    IPosOrder,
    'menuID' | 'orderID' | 'unitPrice' | 'lineRemarks'
  >,
  previousOrder?: Pick<IPosOrder, 'unitPrice' | 'lineRemarks'>,
) => {
  if (firstOrder.menuID !== secondOrder.menuID) return false;

  if (firstOrder.orderID || secondOrder.orderID) {
    return firstOrder.orderID === secondOrder.orderID;
  }

  return (
    isSamePrice(
      firstOrder.unitPrice,
      previousOrder?.unitPrice ?? secondOrder.unitPrice,
    ) &&
    isSameRemarks(
      firstOrder.lineRemarks,
      previousOrder?.lineRemarks ?? secondOrder.lineRemarks,
    )
  );
};

export interface TableArea {
  stationID: string;
  stationName?: string;
  tableAreaID?: string;
  tableAreaDesc: string;
  tableAreaIsActive: number;
}

export interface TransactionDetail {
  pax: number;
  time?: string; // formatted time string e.g. "01:43 PM"
  is_main: number; // 1 = main transaction, 0 = not
  guest_name?: string; // optional guest name
  guest_type: string; // e.g. "In-House", "Walk-In"
  service_option: string; // e.g. "Dine-In", "Take-Out"
  transaction_id: string;
}

export interface TableList {
  stationID: string;
  stationName: string;
  tableAreaID: string;
  tableAreaDesc: string;
  tableAreaIsActive: number;
  tableRefID: string;
  tableAcr: string;
  tableName: string;
  tableIsActive: number;
  transactionDetails: TransactionDetail[];
}

export interface TableDetail {
  stationID: string;
  stationName: string;
  tableAcr: string;
  tableRefID: string;
  transNo: string;
  transactionID: string;
  guestName: string;
  taxTIN: string;
  guestType: string;
  serviceType: string;
  remarks: string;
  totalPax: string;
  timestamp: string;
  nationality: string;
  busName: string;
  busType: string;
  homeAddress: string;
  province: string;
  countryCode: string;
  repExpense: string;
  vatType: string;
  ordersCount: number | null;
  gross: string;
  vat: string;
  vatScLt: string | number;
  serviceCharge: string;
  localTax: string;
  totalDeduction: string;
  netAmount: string;
  isGroupComputation: number;
}

export interface ServiceOption {
  _id: number;
  description: string;
  so_order: number;
  is_active: number;
}

export interface IPaymentDetails {
  transactionID: string;
  card: {
    referenceID: string;
    cardType: string;
    name: string;
    cardDigit: string;
    approvalNumber: string;
    cardHolderName: string;
    amount: string;
    expirationDate: string;
  }[];
  ePayment: {
    referenceID: string;
    cardType: string;
    name: string;
    amount: string;
    remarks: string;
    refNo: string;
    refInvNo?: string;
  }[];
  others: {
    referenceID: string;
    cardType: string;
    name: string;
    amount: string;
    remarks: string;
    issuingBank?: string;
    checkDate?: Moment;
    checkNo?: string;
    giftCertificate?: string;
  }[];
}

export type TransactionDetailWithOrder = TransactionDetail & {
  orders: IPosOrder[];
  // voided: IPosOrder[];
  // isPrintedBill: boolean;
};

export type TransactionDetailWithOrder1 = {
  [transactionID: string]: TransactionDetail & {
    orders: IPosOrder[];
    // voided: IPosOrder[];
    // isPrintedBill: boolean;
  };
};

export interface TableAreaState {
  areas: TableArea[];
  tableList: TableList[];
  tableDetails?: TableDetail;
  selectedTable?: TableList;
  transactionDetails?: TransactionDetailWithOrder;
  tableTransactions?: TransactionDetailWithOrder[];
  serviceOptions: ServiceOption[];
  paymentDetails?: IPaymentDetails[];
  status: {
    isRefetchOrders: boolean;
    noRemoveNewItems: boolean;
  };
  transferTableOrder?: IPosOrder[];
  transferTableID?: string;
  isLoadingTransferOrder?: boolean;
}

const initialState: TableAreaState = {
  areas: [],
  tableList: [],
  tableDetails: undefined,
  selectedTable: undefined,
  transactionDetails: undefined,
  tableTransactions: undefined,
  serviceOptions: [],
  paymentDetails: [],
  status: {
    isRefetchOrders: false,
    noRemoveNewItems: false,
  },
  transferTableOrder: undefined,
  transferTableID: undefined,
  isLoadingTransferOrder: undefined,
};

export const POSTableAreaSlice = createSlice({
  name: 'posTableArea',
  initialState,
  reducers: {
    // TABLE CATEGORY
    setTableAreas(state, action: PayloadAction<TableArea[]>) {
      state.areas = action.payload;
    },

    // TABLE LIST BY CATEGORY
    setTableList(state, action: PayloadAction<TableList[]>) {
      state.tableList = action.payload;

      // if (state.tableDetails) {
      //   const selectedTableDetails = state.tableDetails.transaction_id;

      //   const matchedCurrent = state.tableList.find((table) =>
      //     table.transactionDetails?.some(
      //       (d) => d.transaction_id === selectedTableDetails,
      //     ),
      //   );

      //   if (matchedCurrent) {
      //     state.selectedTable = matchedCurrent;
      //   }
      // }

      if (!state.transferTableID) return;

      const matched = state.tableList.find((table) =>
        table.transactionDetails?.some(
          (d) => d.transaction_id === state.transferTableID,
        ),
      );

      if (matched) {
        state.transferTableID = undefined;
        POSTableAreaSlice.caseReducers.setSelectedTable(state, {
          payload: matched,
          type: 'setSelectedTable',
        });
      }
    },

    // TABLE DETAILS
    setTableDetails(state, action: PayloadAction<TableDetail>) {
      if (
        state.transactionDetails?.transaction_id !==
        action.payload.transactionID
      )
        return;

      state.tableDetails = action.payload;
    },

    // SELECT TABLE
    setSelectedTable(state, action: PayloadAction<TableList>) {
      const newTable = action.payload;
      const currentTable = state.selectedTable;

      const isSameTable = currentTable?.tableRefID === newTable.tableRefID;

      state.selectedTable = newTable;

      if (isSameTable && state.transactionDetails) {
        const existingDetails = state.transactionDetails;
        const updatedMain =
          newTable.transactionDetails?.find((item) => item.is_main) ??
          newTable.transactionDetails?.[
            newTable.transactionDetails?.length - 1
          ];

        state.transactionDetails = {
          ...existingDetails,
          ...updatedMain,
          orders: existingDetails.orders,
          // voided: existingDetails.voided,
          // isPrintedBill: existingDetails.isPrintedBill,
        };
      } else {
        state.transactionDetails = {
          ...(newTable.transactionDetails?.find((item) => item.is_main) ??
            newTable.transactionDetails?.[
              newTable.transactionDetails?.length - 1
            ]),
          orders: [],
          // voided: [],
          // isPrintedBill: false,
        };
      }

      state.tableTransactions = newTable.transactionDetails?.map((item) => {
        const existingTx = state.tableTransactions?.find(
          (t) => t.transaction_id === item.transaction_id,
        );
        return isSameTable && existingTx
          ? { ...existingTx, ...item }
          : {
              ...item,
              orders: [],
              //  voided: [], isPrintedBill: false
            };
      });
    },

    setTransactionDetails(
      state,
      action: PayloadAction<TransactionDetailWithOrder>,
    ) {
      state.transactionDetails = action.payload;
    },

    setTransactionOrders(
      state,
      action: PayloadAction<{
        orders: IPosOrder | IPosOrder[];
        isRefetch?: boolean;
      }>,
    ) {
      const { orders, isRefetch } = action.payload;
      const transaction = state.transactionDetails;
      const noRemoveNewItems = state.status.noRemoveNewItems;

      if (!transaction) return;

      // Convert to array
      const incoming = Array.isArray(orders) ? orders : [orders];

      // Inject transfer orders ONCE
      const currentOrders = transaction.orders ?? [];
      const transferOrders = state.transferTableOrder ?? [];
      const allIncoming = noRemoveNewItems
        ? [
            ...incoming,
            ...transferOrders,
            ...currentOrders?.filter((item) => !item.orderID),
          ]
        : [...incoming, ...transferOrders];

      // After injection, clear transferOrders

      if (isRefetch) {
        transaction.orders = allIncoming;
        state.status.isRefetchOrders = false;
        return;
      }

      const upsertOrders = (newOrders: IPosOrder[]): IPosOrder[] => {
        if (newOrders.length === 0) {
          return currentOrders;
        }

        const updated = [...currentOrders];

        for (const o of newOrders) {
          const idx = updated.findIndex((i) => i.orderID === o.orderID);
          if (idx !== -1) {
            updated[idx] = { ...updated[idx], ...o };
          } else {
            updated.push(o);
          }
        }

        const newIds = new Set(newOrders.map((o) => o.orderID));
        return updated.filter((o) => newIds.has(o.orderID) || !o.orderID);
      };

      transaction.orders = upsertOrders(allIncoming);

      if (transferOrders.length > 0) {
        state.transferTableOrder = [];
      }
    },

    setTransactionOrders1(
      state,
      action: PayloadAction<{
        orders: IPosOrder | IPosOrder[];
        isRefetch?: boolean;
      }>,
    ) {
      const { orders, isRefetch } = action.payload;

      const transaction = state.transactionDetails;

      if (!transaction) return;

      if (isRefetch) {
        const newOrder = Array.isArray(orders) ? orders : [orders];
        transaction.orders = newOrder;
        state.status.isRefetchOrders = false;
        return;
      }

      const currentOrders = transaction.orders ?? [];

      const upsertOrders = (newOrders: IPosOrder[]): IPosOrder[] => {
        if (newOrders.length === 0) {
          transaction.orders = [];
          return [];
        }

        const updated = [...currentOrders];
        for (const newOrder of newOrders) {
          const index = updated.findIndex(
            (o) => o.orderID === newOrder.orderID,
          );
          if (index !== -1) {
            updated[index] = { ...updated[index], ...newOrder };
          } else {
            updated.push(newOrder);
          }
        }
        return updated;
      };

      transaction.orders = Array.isArray(orders)
        ? upsertOrders(orders)
        : upsertOrders([orders]);
    },

    updateTransactionOrder(
      state,
      action: PayloadAction<{
        orderID: string | number;
        changes: Partial<IPosOrder>;
      }>,
    ) {
      if (!state.transactionDetails) return;

      const { orderID, changes } = action.payload;
      const index = state.transactionDetails.orders?.findIndex(
        (o) => o.orderID === orderID,
      );

      if (index !== undefined && index >= 0) {
        state.transactionDetails.orders[index] = {
          ...state.transactionDetails.orders[index],
          ...changes,
        };
      }
    },

    addTableOrder(state, action: PayloadAction<IPosOrder>) {
      const { payload: order } = action;

      const orders = state.transactionDetails?.orders;
      if (!orders) return;

      const orderIndex = orders.findIndex(
        (item) =>
          item.menuID === order.menuID &&
          !item.isPrintedOS &&
          isSamePrice(item.unitPrice, order.unitPrice) &&
          isSameRemarks(item.lineRemarks, order.lineRemarks),
      );

      if (orderIndex === -1) {
        orders.push(order);
      } else {
        const quantity = orders[orderIndex].quantity + order.quantity;
        const amount = order.unitPrice * quantity;

        orders[orderIndex] = {
          ...orders[orderIndex],
          ...order,
          quantity,
          grossAmount: amount,
          netAmount: amount,
        };
      }
    },

    updateTableOrder(
      state,
      action: PayloadAction<
        IPosOrder & {
          previousUnitPrice?: number;
          previousLineRemarks?: string;
        }
      >,
    ) {
      const { previousUnitPrice, previousLineRemarks, ...order } =
        action.payload;

      const orders = state.transactionDetails?.orders;
      if (!orders) return;

      const orderIndex = orders.findIndex(
        (item) =>
          !item.isPrintedOS &&
          isSamePOSOrderLine(item, order, {
            unitPrice: previousUnitPrice ?? order.unitPrice,
            lineRemarks: previousLineRemarks ?? order.lineRemarks,
          }),
      );

      if (orderIndex !== -1) {
        const mergeIndex = orders.findIndex(
          (item, index) =>
            index !== orderIndex &&
            !item.isPrintedOS &&
            !item.orderID &&
            !order.orderID &&
            isSamePOSOrderLine(item, order),
        );

        if (mergeIndex !== -1) {
          const quantity = orders[mergeIndex].quantity + order.quantity;
          const amount = order.unitPrice * quantity;

          orders[mergeIndex] = {
            ...orders[mergeIndex],
            ...order,
            quantity,
            grossAmount: amount,
            netAmount: amount,
          };
          orders.splice(orderIndex, 1);
          return;
        }

        orders[orderIndex] = order;
      }
    },

    removeSelectedTable(state, action: PayloadAction<string | undefined>) {
      const orders = state.transactionDetails?.orders;

      if (orders?.length && action.payload) {
        state.transferTableOrder = orders.filter((item) => !item.orderID);
        state.isLoadingTransferOrder = true;
      }

      state.transferTableID = action.payload;
      state.selectedTable = undefined;
      state.transactionDetails = undefined;
      state.tableTransactions = undefined;
      state.tableDetails = undefined;
    },

    clearTableOrder(
      state,
      action: PayloadAction<
        | {
            menuID?: string;
            orderID?: string | null;
            unitPrice?: number;
            lineRemarks?: string;
            clearNoID?: string[];
          }
        | undefined
      >,
    ) {
      const { menuID, orderID, unitPrice, lineRemarks, clearNoID } =
        action.payload ?? {};

      if (!state.transactionDetails?.orders) return;

      if (clearNoID?.length) {
        // Remove all orders without orderID and same menuID
        state.transactionDetails.orders =
          state.transactionDetails.orders.filter(
            (o) => !(clearNoID.includes(o.menuID) && !o.orderID),
          );
        return;
      }

      if (!menuID) {
        // Clear all orders
        state.transactionDetails.orders =
          state.transactionDetails.orders?.filter(
            (item) => item.isPrintedOS || item.orderID,
          );
      } else {
        // Remove specific order(s)
        state.transactionDetails.orders =
          state.transactionDetails.orders.filter(
            (o) =>
              !isSamePOSOrderLine(o, {
                menuID,
                orderID: orderID ?? '',
                unitPrice: unitPrice ?? o.unitPrice,
                lineRemarks: lineRemarks ?? o.lineRemarks,
              }),
          );
      }
    },

    stopIsLoadingTransferTable(state) {
      state.isLoadingTransferOrder = false;
    },

    // PAYMENT DETAILS

    setCardPaymentDetails: (
      state,
      action: PayloadAction<{
        transactionID: string;
        details: IPaymentDetails['card'][number];
      }>,
    ) => {
      const { transactionID, details } = action.payload;
      const existing = state.paymentDetails?.find(
        (p) => p.transactionID === transactionID,
      );

      if (existing) {
        existing.card.push(details);
      } else {
        state.paymentDetails?.push({
          transactionID,
          card: [details],
          ePayment: [],
          others: [],
        });
      }
    },

    setEPaymentPaymentDetails: (
      state,
      action: PayloadAction<{
        transactionID: string;
        details: IPaymentDetails['ePayment'][number];
      }>,
    ) => {
      const { transactionID, details } = action.payload;
      const existing = state.paymentDetails?.find(
        (p) => p.transactionID === transactionID,
      );

      if (existing) {
        existing.ePayment.push(details);
      } else {
        state.paymentDetails?.push({
          transactionID,
          card: [],
          ePayment: [details],
          others: [],
        });
      }
    },

    setOthersPaymentDetails: (
      state,
      action: PayloadAction<{
        transactionID: string;
        details: IPaymentDetails['others'][number];
      }>,
    ) => {
      const { transactionID, details } = action.payload;
      const existing = state.paymentDetails?.find(
        (p) => p.transactionID === transactionID,
      );

      if (existing) {
        existing.others.push(details);
      } else {
        state.paymentDetails?.push({
          transactionID,
          card: [],
          ePayment: [],
          others: [details],
        });
      }
    },

    clearPaymentDetails: (
      state,
      action: PayloadAction<{ transactionID: string }>,
    ) => {
      state.paymentDetails = state.paymentDetails?.filter(
        (p) => p.transactionID !== action.payload.transactionID,
      );
    },

    deletePaymentDetail: (
      state,
      action: PayloadAction<{
        transactionID: string;
        type?: 'card' | 'ePayment' | 'others';
        index?: number;
        referenceID?: number | string;
      }>,
    ) => {
      const { transactionID, type, index, referenceID } = action.payload;

      const existing = state.paymentDetails?.find(
        (p) => p.transactionID === transactionID,
      );

      if (!existing) return;

      if (!type) {
        existing.card = [];
        existing.ePayment = [];
        existing.others = [];
        return;
      }

      if (type === 'card') {
        if (referenceID !== undefined) {
          existing.card = existing.card.filter(
            (item) => item.referenceID !== referenceID,
          );
        } else if (typeof index === 'number') {
          existing.card.splice(index, 1);
        } else {
          existing.card = [];
        }
      }

      if (type === 'ePayment') {
        if (referenceID !== undefined) {
          existing.ePayment = existing.ePayment.filter(
            (item) => item.referenceID !== referenceID,
          );
        } else if (typeof index === 'number') {
          existing.ePayment.splice(index, 1);
        } else {
          existing.ePayment = [];
        }
      }

      if (type === 'others') {
        if (referenceID !== undefined) {
          existing.others = existing.others.filter(
            (item) => item.referenceID !== referenceID,
          );
        } else if (typeof index === 'number') {
          existing.others.splice(index, 1);
        } else {
          existing.others = [];
        }
      }
    },

    // SERVICE OPTIONS
    setServiceOptions(state, action: PayloadAction<ServiceOption[]>) {
      state.serviceOptions = action.payload;
    },

    // STATUS
    setIsRefetchOrders(state, action: PayloadAction<boolean>) {
      state.status.isRefetchOrders = action.payload;
    },
    setNoRemoveNewItems(state, action: PayloadAction<boolean>) {
      state.status.noRemoveNewItems = action.payload;
    },

    // CLEAR
    clearPOSTableArea(state, action: PayloadAction<boolean | undefined>) {
      if (action.payload) {
        return initialState;
      }
      return {
        ...initialState,
        paymentDetails: state.paymentDetails,
      };
    },

    clearPOSSelectedTable(state) {
      state.selectedTable = undefined;
      state.transactionDetails = undefined;
      state.tableTransactions = undefined;
      state.tableDetails = undefined;
    },
  },
});

export const {
  setTableAreas,
  setTableList,
  setTableDetails,
  setSelectedTable,
  setServiceOptions,
  clearPOSTableArea,
  setTransactionOrders,
  addTableOrder,
  clearTableOrder,
  updateTableOrder,
  setTransactionDetails,
  setCardPaymentDetails,
  setEPaymentPaymentDetails,
  setOthersPaymentDetails,
  clearPaymentDetails,
  deletePaymentDetail,
  removeSelectedTable,
  updateTransactionOrder,
  setIsRefetchOrders,
  stopIsLoadingTransferTable,
  clearPOSSelectedTable,
  setNoRemoveNewItems,
} = POSTableAreaSlice.actions;
export default POSTableAreaSlice.reducer;
